import React from 'react';
import { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FiMessageCircle, FiX, FiSend, FiMail, FiGithub, FiLinkedin, FiClock, FiBriefcase } from 'react-icons/fi';

const ChatButton = styled(motion.button)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  border: none;
  color: white;
  font-size: 1.5rem;
  cursor: pointer;
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.3);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(139, 92, 246, 0.4);
  }
`;

const ChatModal = styled(motion.div)`
  position: fixed;
  bottom: 5rem;
  right: 2rem;
  width: 350px;
  max-height: 500px;
  background: linear-gradient(135deg, rgba(26, 26, 46, 0.95), rgba(139, 92, 246, 0.1));
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  z-index: 1001;
  overflow: hidden;

  @media (max-width: 768px) {
    bottom: 5rem;
    right: 1rem;
    left: 1rem;
    width: auto;
    max-height: 70vh;
  }
`;

const ChatHeader = styled.div`
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  color: white;
  padding: 1rem 1.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ChatTitle = styled.h3`
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: 50%;
  transition: all 0.3s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
  }
`;

const ChatBody = styled.div`
  padding: 1.5rem;
  max-height: 300px;
  overflow-y: auto;

  @media (max-width: 768px) {
    padding: 1rem;
    max-height: 50vh;
  }
`;

const WelcomeMessage = styled.div`
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const WelcomeTitle = styled.h4`
  color: #8b5cf6;
  margin: 0 0 0.5rem 0;
  font-size: 1rem;
`;

const WelcomeText = styled.p`
  color: #94a3b8;
  margin: 0;
  font-size: 0.9rem;
  line-height: 1.4;
`;

const QuickActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
  margin-bottom: 1.5rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
`;

const QuickAction = styled(motion.button)`
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);
  border-radius: 8px;
  padding: 0.75rem;
  color: #8b5cf6;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  font-weight: 500;

  &:hover {
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.4);
  }

  @media (max-width: 768px) {
    flex-direction: row;
    justify-content: flex-start;
    padding: 0.5rem;
    font-size: 0.75rem;
  }
`;

const ContactInfo = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 1rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 0.75rem;
  color: #e2e8f0;
  font-size: 0.9rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactIcon = styled.div`
  color: #8b5cf6;
  font-size: 1rem;
`;

const ContactLink = styled.a`
  color: #8b5cf6;
  text-decoration: none;
  font-weight: 500;

  &:hover {
    color: #ec4899;
    text-decoration: underline;
  }
`;

const StatusIndicator = styled.div<{ isOnline: boolean }>`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: ${props => props.isOnline ? '#10b981' : '#94a3b8'};
`;

const StatusDot = styled.div<{ isOnline: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.isOnline ? '#10b981' : '#94a3b8'};
  animation: ${props => props.isOnline ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.5; }
  }
`;

const ChatInput = styled.div`
  padding: 1rem 1.5rem;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  gap: 0.75rem;
  align-items: center;

  @media (max-width: 768px) {
    padding: 1rem;
    gap: 0.5rem;
  }
`;

const Input = styled.input`
  flex: 1;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(139, 92, 246, 0.3);
  border-radius: 8px;
  padding: 0.75rem;
  color: #ffffff;
  font-size: 0.9rem;
  outline: none;

  &:focus {
    border-color: #8b5cf6;
  }

  &::placeholder {
    color: #94a3b8;
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
    font-size: 0.85rem;
  }
`;

const SendButton = styled(motion.button)`
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  border: none;
  border-radius: 8px;
  padding: 0.75rem;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }

  @media (max-width: 768px) {
    padding: 0.5rem;
  }
`;

const MAX_QUESTIONS = 3;

const LiveChat: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [isOnline] = useState(true); // Simulate online status
  const [chatHistory, setChatHistory] = useState<{role: 'user'|'assistant', content: string}[]>([]);
  const [questionCount, setQuestionCount] = useState<number>(() => {
    const saved = localStorage.getItem('chatbot_question_count');
    return saved ? parseInt(saved, 10) : 0;
  });
  const [isLoading, setIsLoading] = useState(false);

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'contact':
        if (window.location.pathname === '/') {
          document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.location.href = '/#contact';
        }
        break;
      case 'github':
        window.open('https://github.com/briddytech', '_blank');
        break;
      case 'linkedin':
        window.open('https://linkedin.com/in/briddytech', '_blank');
        break;
      case 'projects':
        if (window.location.pathname === '/') {
          document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' });
        } else {
          window.location.href = '/#projects';
        }
        break;
    }
    setIsOpen(false);
  };

  const handleSendMessage = async () => {
    if (!message.trim() || isLoading || questionCount >= MAX_QUESTIONS) return;
    const newHistory = [...chatHistory, { role: 'user' as 'user', content: message }];
    setChatHistory(newHistory);
    setMessage('');
    setIsLoading(true);
    try {
      const response = await fetch('/.netlify/functions/chatgpt-proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          messages: newHistory.map(m => ({ role: m.role, content: m.content })),
        }),
      });
      const data = await response.json();
      const botReply = data.choices?.[0]?.message?.content || 'Sorry, I could not get a response.';
      setChatHistory([...newHistory, { role: 'assistant' as 'assistant', content: botReply }]);
      const newCount = questionCount + 1;
      setQuestionCount(newCount);
      localStorage.setItem('chatbot_question_count', newCount.toString());
    } catch {
      setChatHistory([...newHistory, { role: 'assistant' as 'assistant', content: 'Sorry, there was an error contacting the AI.' }]);
    }
    setIsLoading(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Reset question count when chat is closed
  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <>
      <ChatButton
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        animate={{
          scale: isOpen ? 0.9 : 1,
          rotate: isOpen ? 180 : 0
        }}
        transition={{ type: "spring", stiffness: 300, damping: 20 }}
      >
        <FiMessageCircle />
      </ChatButton>

      <AnimatePresence>
        {isOpen && (
          <ChatModal
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <ChatHeader>
              <div>
                <ChatTitle>Chat with Briddy's Chatbot</ChatTitle>
                <StatusIndicator isOnline={isOnline}>
                  <StatusDot isOnline={isOnline} />
                  {isOnline ? 'Online' : 'Offline'}
                </StatusIndicator>
              </div>
              <CloseButton onClick={handleClose}>
                <FiX />
              </CloseButton>
            </ChatHeader>

            <ChatBody>
              <WelcomeMessage>
                <WelcomeTitle>👋 Hi there!</WelcomeTitle>
                <WelcomeText>
                  I'm Briddy's Chatbot powered by ChatGPT. You can ask up to 3 questions per visit.
                </WelcomeText>
              </WelcomeMessage>

              {chatHistory.map((msg, idx) => (
                <div key={idx} style={{
                  background: msg.role === 'user' ? 'rgba(139,92,246,0.08)' : 'rgba(236,72,153,0.08)',
                  color: msg.role === 'user' ? '#fff' : '#8b5cf6',
                  borderRadius: 8,
                  padding: '0.5rem 0.75rem',
                  marginBottom: 8,
                  alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                  maxWidth: '90%',
                  fontSize: '0.95rem',
                }}>
                  <b>{msg.role === 'user' ? 'You' : 'BriddyBot'}:</b> {msg.content}
                </div>
              ))}

              <QuickActions>
                <QuickAction
                  onClick={() => handleQuickAction('contact')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiMail size={20} />
                  Contact Form
                </QuickAction>
                <QuickAction
                  onClick={() => handleQuickAction('github')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiGithub size={20} />
                  View GitHub
                </QuickAction>
                <QuickAction
                  onClick={() => handleQuickAction('linkedin')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiLinkedin size={20} />
                  LinkedIn
                </QuickAction>
                <QuickAction
                  onClick={() => handleQuickAction('projects')}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <FiBriefcase size={20} />
                  View Projects
                </QuickAction>
              </QuickActions>

              <ContactInfo>
                <ContactItem>
                  <ContactIcon><FiMail /></ContactIcon>
                  <ContactLink href="mailto:briddy.tech@gmail.com">
                    Email
                  </ContactLink>
                </ContactItem>
                <ContactItem>
                  <ContactIcon><FiClock /></ContactIcon>
                  <span>Response time: Usually within 24 hours</span>
                </ContactItem>
              </ContactInfo>
            </ChatBody>

            <ChatInput>
              <Input
                placeholder={questionCount >= MAX_QUESTIONS ? "Question limit reached (3 per visit)" : "Type your message..."}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                disabled={questionCount >= MAX_QUESTIONS || isLoading}
              />
              <SendButton
                onClick={handleSendMessage}
                disabled={!message.trim() || questionCount >= MAX_QUESTIONS || isLoading}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FiSend size={16} />
              </SendButton>
            </ChatInput>
            {questionCount >= MAX_QUESTIONS && (
              <div style={{ color: '#ec4899', textAlign: 'center', padding: '0.5rem 1rem 1rem 1rem', fontWeight: 500 }}>
                You have reached the maximum of 3 questions for this visit.
              </div>
            )}
          </ChatModal>
        )}
      </AnimatePresence>
    </>
  );
};

export default LiveChat; 